import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { clubsBookingApi, type AdminSubscriptionAccessStatus } from '@/api/clubs-booking';
import { auth } from '@/store/authStore';
import { useAuth } from '@/contexts/AuthContext';

const AdminSubscriptionRequiredPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = auth.getUser();
  const { handleLogout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [access, setAccess] = useState<AdminSubscriptionAccessStatus | null>(null);

  const checkoutState = searchParams.get('checkout');

  useEffect(() => {
    if (user.role === 'parent') return;

    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const status = await clubsBookingApi.getAdminSubscriptionAccessStatus(true, true);
        if (!active) return;
        setAccess(status);
        if (status.hasActiveSubscription) {
          navigate('/dashboard', { replace: true });
        }
      } catch (err: any) {
        if (!active) return;
        const message = err?.response?.data?.message;
        setError(Array.isArray(message) ? message.join(', ') : message || 'Could not load subscription status');
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [navigate, user.role]);

  const trialDaysLeft = useMemo(() => {
    if (!access) return 0;
    return Math.max(0, Number(access.trialDaysRemaining || 0));
  }, [access]);

  const startCheckout = async () => {
    setBusy(true);
    setError('');
    try {
      const successUrl = `${window.location.origin}/billing-required?checkout=success`;
      const cancelUrl = `${window.location.origin}/billing-required?checkout=cancel`;
      const response = await clubsBookingApi.createAdminSubscriptionCheckout({ successUrl, cancelUrl });
      if (response.url) {
        window.location.assign(response.url);
        return;
      }
      setError('Unable to open Stripe checkout. Please try again.');
      setBusy(false);
    } catch (err: any) {
      const message = err?.response?.data?.message;
      setError(Array.isArray(message) ? message.join(', ') : message || 'Could not start Stripe checkout');
      setBusy(false);
    }
  };

  if (user.role === 'parent') {
    return <Navigate to="/my-bookings" replace />;
  }

  return (
    <main className="min-h-screen bg-[#f1f2f7] px-4 py-10">
      <section className="mx-auto w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <div className="mb-5 flex items-center gap-3">
          <img src="/logo.png" alt="Session Monkey" className="h-10 w-10 rounded-md border border-slate-200 bg-white object-contain p-1" />
          <div>
            <p className="text-xl font-semibold text-slate-900">Session Monkey</p>
            <p className="text-sm text-slate-500">Admin subscription</p>
          </div>
        </div>

        <h1 className="text-3xl font-semibold text-slate-900">Continue your admin access</h1>
        <p className="mt-2 text-slate-600">
          Start a Stripe subscription at <strong>£20/month</strong> to keep your admin dashboard and public booking pages live.
        </p>
        <p className="mt-1 text-xs text-slate-500">If you have a friend discount code, you can apply it directly in Stripe checkout.</p>

        {checkoutState === 'success' && (
          <p className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            Stripe checkout completed. We are confirming your subscription now.
          </p>
        )}
        {checkoutState === 'cancel' && (
          <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            Checkout was cancelled. You can restart whenever you are ready.
          </p>
        )}
        {error && (
          <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
        )}

        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
          {loading && <p className="text-sm text-slate-600">Checking your current trial and subscription status...</p>}
          {!loading && access && (
            <>
              <p className="text-sm text-slate-700">
                {access.trialActive
                  ? `Trial active: ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} remaining.`
                  : 'Trial ended. A subscription is required to continue.'}
              </p>
              <p className="mt-1 text-xs text-slate-500">No setup fees. Cancel anytime in Stripe.</p>
            </>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => void startCheckout()}
            disabled={busy}
            className="rounded-lg bg-[#1F8F5F] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#17754D] disabled:opacity-70"
          >
            {busy ? 'Opening Stripe...' : 'Start £20/month subscription'}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Refresh status
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="text-sm text-slate-600 underline"
          >
            Log out and go to homepage
          </button>
        </div>
      </section>
    </main>
  );
};

export default AdminSubscriptionRequiredPage;
